import { useState, useMemo } from "react";
import { Plus, Building2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MembershipCard } from "@/components/sacco-management/MembershipCard";
import { MembershipForm } from "@/components/sacco-management/MembershipForm";
import { CurrencyToggle } from "@/components/currency-toggle/CurrencyToggle";
import { useFetchSaccoMemberships } from "@saccoHooks";
import {
  useFetchSaccoLoanMembershipRepayments,
  useSaccoMetrics,
  useFetchSaccoShares,
  useFetchSaccoWithdrawals,
  useFetchSaccoDeposits
} from "@saccoHooks";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import {SaccoMembership} from "@/@types/sacco.models.ts";

function formatCurrency(value: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

function useMembershipMetrics(membershipId: string) {
  const { data: deposits = [] } = useFetchSaccoDeposits(membershipId);
  const { data: withdrawals = [] } = useFetchSaccoWithdrawals(membershipId);
  const { data: shares = [] } = useFetchSaccoShares(membershipId);
  const { data: repayments = [] } = useFetchSaccoLoanMembershipRepayments(membershipId);
  return useSaccoMetrics({ deposits, withdrawals, shares, repayments });
}

// Single-membership metrics row — rendered once per membership to feed aggregate.
function MembershipMetricsRow({
  membership,
  displayCurrency,
  convert,
  isLoading: ratesLoading,
  error: ratesError,
  onMetrics,
}: {
  membership: SaccoMembership;
  displayCurrency: string;
  convert: (amount: number, from: string, to: string) => number;
  isLoading: boolean;
  error: Error | null;
  onMetrics: (id: string, convertedNetPosition: number) => void;
}) {
  const metrics = useMembershipMetrics(membership.id);
  const nativeCurrency = membership.currency ?? 'KES';

  let convertedNetPosition = metrics.netPosition;
  if (!ratesError && !ratesLoading && displayCurrency !== nativeCurrency) {
    try {
      convertedNetPosition = convert(metrics.netPosition, nativeCurrency, displayCurrency);
    } catch {
      // fall back to raw amount
    }
  }

  onMetrics(membership.id, convertedNetPosition);
  return null;
}

function AggregateInner({
  memberships,
  displayCurrency,
  convert,
  isLoading: ratesLoading,
  error: ratesError,
}: {
  memberships: SaccoMembership[];
  displayCurrency: string;
  convert: (amount: number, from: string, to: string) => number;
  isLoading: boolean;
  error: Error | null;
}) {
  const metricsMap: Record<string, number> = {};

  function handleMetrics(id: string, convertedNetPosition: number) {
    metricsMap[id] = convertedNetPosition;
  }

  const totalNetPosition = Object.values(metricsMap).reduce((s, v) => s + v, 0);

  return (
    <>
      {memberships.map((membership) => (
        <MembershipMetricsRow
          key={membership.id}
          membership={membership}
          displayCurrency={displayCurrency}
          convert={convert}
          isLoading={ratesLoading}
          error={ratesError}
          onMetrics={handleMetrics}
        />
      ))}
      <Card className="mb-6 bg-muted/40">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Net Position</p>
              <p
                className={`text-2xl font-bold ${
                  totalNetPosition >= 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                {ratesLoading ? (
                  <Skeleton className="h-8 w-32 inline-block" />
                ) : (
                  formatCurrency(totalNetPosition, ratesError ? 'KES' : displayCurrency)
                )}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Memberships</p>
              <p className="text-2xl font-bold">{memberships.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

/** Computes aggregate net position across all memberships. */
function AggregateSummary({
  memberships,
  displayCurrency,
  convert,
  isLoading,
  error,
}: {
  memberships: SaccoMembership[];
  displayCurrency: string;
  convert: (amount: number, from: string, to: string) => number;
  isLoading: boolean;
  error: Error | null;
}) {
  return (
    <AggregateInner
      memberships={memberships}
      displayCurrency={displayCurrency}
      convert={convert}
      isLoading={isLoading}
      error={error}
    />
  );
}

export default function SaccoListPage() {
  const [addOpen, setAddOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [displayCurrency, setDisplayCurrency] = useState('KES');
  const { data: memberships = [], isLoading } = useFetchSaccoMemberships();
  const { convert, isLoading: ratesLoading, error: ratesError } = useExchangeRates('KES');

  const filteredMemberships = useMemo(() => {
    if (!searchQuery) return memberships;
    const lowerQuery = searchQuery.toLowerCase();
    return memberships.filter(m => 
      m.institution_name?.toLowerCase().includes(lowerQuery) ||
      m.member_number?.toLowerCase().includes(lowerQuery)
    );
  }, [memberships, searchQuery]);

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Sacco Memberships</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage your Sacco memberships, deposits, shares, and loans.
          </p>
        </div>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center mt-4 sm:mt-0 w-full lg:w-auto">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search saccos..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <CurrencyToggle
              baseCurrency="KES"
              selectedCurrency={displayCurrency}
              onCurrencyChange={setDisplayCurrency}
              showBalance={false}
            />
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Membership
            </Button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48 w-full" />
            ))}
          </div>
        </div>
      ) : filteredMemberships.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
          <h2 className="text-lg font-semibold mb-2">No memberships yet</h2>
          <p className="text-muted-foreground mb-4 max-w-sm">
            Add your first Sacco membership to start tracking deposits, shares, and loans.
          </p>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Membership
          </Button>
        </div>
      ) : (
        <>
          <AggregateSummary
            memberships={filteredMemberships}
            displayCurrency={displayCurrency}
            convert={convert}
            isLoading={ratesLoading}
            error={ratesError}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMemberships.map((membership) => (
              <MembershipCard
                key={membership.id}
                membership={membership}
                displayCurrency={displayCurrency}
                convert={convert}
                isLoading={ratesLoading}
                error={ratesError}
              />
            ))}
          </div>
        </>
      )}

      <MembershipForm open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
